/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The company logo, for the Templates "Brand & logo" panel. The same calls as
 * the logo upload in Company Settings (layouts/company-settings): the logo is
 * the latest image attachment on company_info, and its storage refs are also
 * saved on company_info so PDFs can fetch the private file later.
 */

import { useCallback, useEffect, useState } from "react";
import {
  deleteImageObject,
  fetchS3Attachments,
  getImageAttachment,
  getImageURL,
  uploadReplaceImage,
} from "utils/S3";

export default function useCompanyLogo(axiosInstance, companyId) {
  const [attachments, setAttachments] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    fetchS3Attachments({ axiosInstance, parent_id: companyId, parent_table: "company_info", updateAttachments: setAttachments });
  }, [axiosInstance, companyId]);

  useEffect(() => {
    let cancelled = false;
    getImageURL(attachments, axiosInstance).then((url) => {
      if (!cancelled) setPreviewUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [attachments, axiosInstance]);

  /** After AttachmentUploadModal's onUpload. Returns false if the PDF refs did not save. */
  const upload = useCallback(
    async (uploadData, closeModal) => {
      setBusy(true);
      try {
        await uploadReplaceImage({
          axiosInstance,
          updateIsAttachmentModalOpen: closeModal,
          attachments,
          updateAttachments: setAttachments,
          uploadData,
          parent_table: "company_info",
          parent_id: companyId,
        });
        await axiosInstance.put("/company/info", {
          logo_url: uploadData.url || null,
          logo_bucket: uploadData.bucket || null,
          logo_storage_path: uploadData.path || null,
        });
        return true;
      } catch (error) {
        console.error("Error saving company logo:", error);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [axiosInstance, attachments, companyId]
  );

  const remove = useCallback(async () => {
    setBusy(true);
    try {
      const linked = getImageAttachment(attachments);
      if (linked) await deleteImageObject(axiosInstance, linked, setAttachments);
      setPreviewUrl(null);
      await axiosInstance.put("/company/info", { logo_url: null, logo_bucket: null, logo_storage_path: null });
      return true;
    } catch (error) {
      console.error("Error removing company logo:", error);
      return false;
    } finally {
      setBusy(false);
    }
  }, [axiosInstance, attachments]);

  return { previewUrl, busy, upload, remove };
}
